/**
 * Money is always integer cents. Never use floats for stored or compared amounts.
 */
export type Currency = "USD";

export interface Money {
  readonly cents: number;
  readonly currency: Currency;
}

/** Largest amount we accept: $10 billion, well inside Number.MAX_SAFE_INTEGER. */
export const MAX_CENTS = 1_000_000_000_000;

export class MoneyError extends Error {
  override readonly name = "MoneyError";
}

/** Safe text for an error message: never throws (Symbols, throwing toString, revoked proxies). */
function show(v: unknown): string {
  try {
    if (typeof v === "symbol") return v.toString();
    if (typeof v === "string") return v.length > 64 ? `${v.slice(0, 64)}...` : v;
    if (typeof v === "number" || typeof v === "bigint" || typeof v === "boolean") return String(v);
    if (v === null || v === undefined) return String(v);
    return `[${typeof v}]`;
  } catch {
    return "[unprintable]";
  }
}

function assertCents(cents: number): void {
  if (!Number.isInteger(cents))
    throw new MoneyError(`cents must be an integer, got ${show(cents)}`);
  if (Math.abs(cents) > MAX_CENTS) throw new MoneyError(`amount out of range: ${show(cents)}`);
}

/**
 * Reject anything that is not a well-formed USD Money (null, wrong currency, float / NaN / huge cents,
 * throwing getters) with a MoneyError, so helpers never throw a TypeError/RangeError or emit bad cents.
 */
function money(a: unknown, what: string): Money {
  if (a === null || typeof a !== "object")
    throw new MoneyError(`${what}: not a Money value: ${show(a)}`);
  let cents: unknown;
  let currency: unknown;
  try {
    cents = (a as Money).cents;
    currency = (a as Money).currency;
  } catch {
    throw new MoneyError(`${what}: unreadable Money value`);
  }
  if (currency !== "USD") throw new MoneyError(`${what}: unsupported currency ${show(currency)}`);
  if (typeof cents !== "number")
    throw new MoneyError(`${what}: cents must be a number, got ${show(cents)}`);
  assertCents(cents);
  return a as Money;
}

/** Round half away from zero (commercial rounding), stable for negatives. */
export function roundHalfAwayFromZero(n: number): number {
  if (typeof n !== "number" || !Number.isFinite(n))
    throw new MoneyError(`not a finite number: ${show(n)}`);
  const r = Math.sign(n) * Math.round(Math.abs(n));
  return r === 0 ? 0 : r; // normalize -0
}

export function usd(cents: number): Money {
  if (typeof cents !== "number") throw new MoneyError(`cents must be a number, got ${show(cents)}`);
  assertCents(cents);
  return Object.freeze({ cents: cents === 0 ? 0 : cents, currency: "USD" as const });
}

export const ZERO: Money = usd(0);

/** An exact decimal value num / 10^scale (BigInt, no float error). */
interface Decimal {
  readonly num: bigint;
  readonly scale: number;
}

const DECIMAL_RE = /^(-?)(\d+)(?:\.(\d+))?(?:e([+-]?\d+))?$/i;

/**
 * Exact decimal for a plain decimal string, or for a JS number via its shortest round-trip string
 * (so 1.005 means the 1.005 the caller wrote, not the binary 1.00499999...).
 */
function toDecimal(text: string): Decimal | null {
  const m = DECIMAL_RE.exec(text);
  if (!m) return null;
  const [, sign, whole = "0", frac = "", expText] = m;
  const exp = expText === undefined ? 0 : Number(expText);
  if (!Number.isSafeInteger(exp) || Math.abs(exp) > 400) return null;
  let num = BigInt(whole + frac);
  let scale = frac.length - exp;
  if (scale < 0) {
    num *= 10n ** BigInt(-scale);
    scale = 0;
  }
  return { num: sign === "-" ? -num : num, scale };
}

function numberToDecimal(n: number, what: string): Decimal {
  if (typeof n !== "number" || !Number.isFinite(n)) {
    throw new MoneyError(`${what}: not a finite number: ${show(n)}`);
  }
  const d = toDecimal(String(n));
  if (!d) throw new MoneyError(`${what}: unsupported number ${show(n)}`);
  return d;
}

/** Round num / den half away from zero, exactly, into in-range integer cents. */
function roundRatio(num: bigint, den: bigint): number {
  const neg = num < 0n !== den < 0n;
  const n = num < 0n ? -num : num;
  const d = den < 0n ? -den : den;
  const q = (2n * n + d) / (2n * d);
  if (q > BigInt(MAX_CENTS))
    throw new MoneyError(`amount out of range: ${neg ? "-" : ""}${q} cents`);
  const r = Number(q);
  return neg && r !== 0 ? -r : r;
}

/** cents * (d.num / 10^d.scale) / extraDen, rounded half away from zero. */
function scaleCents(cents: number, d: Decimal, extraDen: bigint): Money {
  return usd(roundRatio(BigInt(cents) * d.num, 10n ** BigInt(d.scale) * extraDen));
}

/** Parse a dollar amount (number or "1,234.56" / "$12.30" string) into cents, exactly. */
export function fromDollars(dollars: number | string): Money {
  let d: Decimal;
  if (typeof dollars === "string") {
    // Cap length before regex/BigInt work: anything this long is far past MAX_CENTS anyway.
    const cleaned = dollars.length > 64 ? "" : dollars.trim().replace(/[$,\s]/g, "");
    const parsed = /^-?\d+(\.\d+)?$/.test(cleaned) ? toDecimal(cleaned) : null;
    if (!parsed) throw new MoneyError(`invalid dollar amount: ${show(dollars)}`);
    d = parsed;
  } else {
    d = numberToDecimal(dollars, "invalid dollar amount");
  }
  return scaleCents(100, d, 1n);
}

function sameCurrency(a: Money, b: Money): void {
  money(a, "left operand");
  money(b, "right operand");
}

export function add(a: Money, b: Money): Money {
  sameCurrency(a, b);
  return usd(a.cents + b.cents);
}

export function subtract(a: Money, b: Money): Money {
  sameCurrency(a, b);
  return usd(a.cents - b.cents);
}

export function sum(items: readonly Money[]): Money {
  if (!Array.isArray(items)) throw new MoneyError(`sum: expected an array, got ${show(items)}`);
  return items.reduce<Money>((acc, m) => add(acc, m), ZERO);
}

/** Multiply by a ratio (e.g. 1.15 for +15%), exactly, rounding half away from zero. */
export function multiply(a: Money, factor: number): Money {
  return scaleCents(money(a, "multiply").cents, numberToDecimal(factor, "invalid factor"), 1n);
}

/** Percentage of an amount, where pct=15 means 15%. Exact: no pct/100 float step. */
export function percentOf(a: Money, pct: number): Money {
  return scaleCents(money(a, "percentOf").cents, numberToDecimal(pct, "invalid percent"), 100n);
}

export function compare(a: Money, b: Money): -1 | 0 | 1 {
  sameCurrency(a, b);
  return a.cents < b.cents ? -1 : a.cents > b.cents ? 1 : 0;
}

export function isNegative(a: Money): boolean {
  return money(a, "isNegative").cents < 0;
}

export function max(a: Money, b: Money): Money {
  return compare(a, b) >= 0 ? a : b;
}

export function min(a: Money, b: Money): Money {
  return compare(a, b) <= 0 ? a : b;
}

/** "$1,234.56" / "-$5.00". Display only; never parse this back for math. */
export function format(a: Money): string {
  const c = money(a, "format").cents;
  const abs = Math.abs(c);
  const dollars = Math.floor(abs / 100).toLocaleString("en-US");
  const cents = String(abs % 100).padStart(2, "0");
  return `${c < 0 ? "-" : ""}$${dollars}.${cents}`;
}
